import React, { useState, useEffect } from 'react';
import { 
  Box, FormControl, InputLabel, Select, MenuItem, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Typography, Grid, Card, IconButton, TextField, 
  InputAdornment, Tooltip, Avatar, alpha, Stack, Divider,
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Fade, Zoom, LinearProgress, TablePagination
} from '@mui/material';
import { 
  ChevronLeft, ChevronRight, CalendarToday, Business, FilterList,
  FileDownload, Receipt, CheckCircle, Warning, ErrorOutline,
  CreditCard, AccountBalance, AttachMoney, Search, DateRange,
  Add, Edit, Delete, Visibility, Close, Save, Event, Numbers,
  LocalAtm, ReceiptLong, WarningAmber, CheckCircleOutline,
  DeleteForever, Cancel
} from '@mui/icons-material';
import { query, collection, where, getDocs, getFirestore, doc, updateDoc, deleteDoc, addDoc } from 'firebase/firestore';
import * as XLSX from 'xlsx';

// Helper function to parse numbers with comma or dot
const parseNumber = (value) => {
  if (!value || value === '') return 0;
  const stringValue = value.toString().replace(',', '.');
  const parsed = parseFloat(stringValue);
  return isNaN(parsed) ? 0 : parsed;
};

// Helper function to format number with 3 decimals
const formatNumber = (value) => {
  const num = typeof value === 'number' ? value : parseNumber(value);
  return num.toFixed(3);
};

// Status Chip Component - Smaller version
const StatusChip = ({ status }) => {
  const statusConfig = {
    'Payée': {
      icon: <CheckCircleOutline fontSize="small" />,
      gradient: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)'
    },
    'Non Payée': {
      icon: <ErrorOutline fontSize="small" />,
      gradient: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)'
    },
    'En retard': {
      icon: <WarningAmber fontSize="small" />,
      gradient: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)'
    }
  };

  const config = statusConfig[status] || statusConfig['Non Payée'];

  return (
    <Chip
      icon={config.icon}
      label={status}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: '0.65rem',
        height: 22,
        borderRadius: '6px',
        color: 'white',
        background: config.gradient,
        '& .MuiChip-icon': { color: 'white', fontSize: '0.9rem' }
      }}
    />
  );
};

const ExcelTableFournisseur = () => {
  const [factures, setFactures] = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [searchType, setSearchType] = useState('');
  const [searchStatue, setSearchStatue] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedFacture, setSelectedFacture] = useState(null);
  const [selectedFournisseur, setSelectedFournisseur] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const restaurantId = localStorage.getItem("restaurantId");
  const db = getFirestore();

  // Form state for add/edit
  const [formData, setFormData] = useState({
    numero: '',
    achatHTVA: '',
    tvaDeductible: '',
    achatTTC: '',
    montantPaye: '',
    statut: 'Non Payée',
    resteAPayer: '',
    modeDePaiement: '',
    paymentnum: '',
    date: new Date().toISOString().split('T')[0],
    retouralasource: '',
    fournisseurId: ''
  });

  // Fetch data
  const fetchData = async () => {
    setLoading(true);
    try {
      const fournisseursQuery = query(
        collection(db, "fournisseurs"),
        where("restaurantId", "==", restaurantId)
      );
      const fournisseursSnapshot = await getDocs(fournisseursQuery);
      const fournisseursData = fournisseursSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setFournisseurs(fournisseursData);

      const facturesQuery = query(
        collection(db, "factures"),
        where("restaurantId", "==", restaurantId)
      );
      const facturesSnapshot = await getDocs(facturesQuery);
      
      const facturesData = facturesSnapshot.docs.map(doc => {
        const data = doc.data();
        const date = data.date?.toDate ? data.date.toDate() : new Date(data.date);
        return { id: doc.id, ...data, date };
      });

      setFactures(facturesData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (restaurantId) fetchData();
  }, [restaurantId]);

  // Calculate TTC and Reste with 3 decimal places
  useEffect(() => {
    const htva = parseNumber(formData.achatHTVA);
    const tva = parseNumber(formData.tvaDeductible);
    const ttc = htva + tva;
    const paye = parseNumber(formData.montantPaye);
    const retenu = parseNumber(formData.retouralasource);
    const reste = ttc - paye - retenu;
    
    setFormData(prev => ({
      ...prev,
      achatTTC: formatNumber(ttc),
      resteAPayer: formatNumber(reste),
      statut: reste <= 0 ? 'Payée' : (reste < ttc ? 'En retard' : 'Non Payée')
    }));
  }, [formData.achatHTVA, formData.tvaDeductible, formData.montantPaye, formData.retouralasource]);

  // Get month range
  const getMonthRange = () => {
    const startDate = new Date(currentYear, currentMonth, 1);
    const endDate = new Date(currentYear, currentMonth + 1, 0);
    endDate.setHours(23, 59, 59, 999);
    return { startDate, endDate };
  };

  // Filter factures
  const { startDate, endDate } = getMonthRange();
  
  const filteredFactures = factures.filter(facture => {
    if (searchType && fournisseurs.find(f => f.id === facture.fournisseurId)?.name !== searchType) {
      return false;
    }
    if (searchStatue && facture.statut !== searchStatue) {
      return false;
    }
    const factureDate = new Date(facture.date);
    return factureDate >= startDate && factureDate <= endDate;
  });

  // Pagination
  const paginatedFactures = filteredFactures.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Export to Excel
  const exportToExcel = () => {
    const tableData = filteredFactures.map(facture => ({
      Date: facture.date.toLocaleDateString('fr-FR'),
      Fournisseur: fournisseurs.find(f => f.id === facture.fournisseurId)?.name || 'Inconnu',
      "Numéro Facture": facture.numero,
      "Achat HTVA": parseFloat(facture.achatHTVA).toFixed(3),
      "TVA Déductible": parseFloat(facture.tvaDeductible).toFixed(3),
      "Achat TTC": parseFloat(facture.achatTTC).toFixed(3),
      "Montant Payé": parseFloat(facture.montantPaye).toFixed(3),
      "Retenu à la source": parseFloat(facture.retouralasource || 0).toFixed(3),
      "Statut": facture.statut,
      "Reste à Payer": parseFloat(facture.resteAPayer).toFixed(3),
      "Mode de paiement": facture.modeDePaiement,
      "Numero de Payment": facture.paymentnum || ''
    }));

    const worksheet = XLSX.utils.json_to_sheet(tableData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Factures");
    XLSX.writeFile(workbook, `Factures_${currentMonth + 1}_${currentYear}.xlsx`);
  };

  // CRUD Operations
  const handleAddFacture = async () => {
    if (!formData.numero || !formData.achatHTVA || !formData.fournisseurId) {
      alert("Veuillez remplir tous les champs obligatoires");
      return;
    }

    try {
      const factureData = {
        fournisseurId: formData.fournisseurId,
        restaurantId: restaurantId,
        numero: formData.numero,
        achatHTVA: parseNumber(formData.achatHTVA),
        tvaDeductible: parseNumber(formData.tvaDeductible),
        achatTTC: parseNumber(formData.achatTTC),
        montantPaye: parseNumber(formData.montantPaye),
        statut: formData.statut,
        resteAPayer: parseNumber(formData.resteAPayer),
        modeDePaiement: formData.modeDePaiement,
        paymentnum: formData.paymentnum || '',
        date: formData.date,
        retouralasource: parseNumber(formData.retouralasource)
      };

      await addDoc(collection(db, "factures"), factureData);
      setOpenAddDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error adding facture:", error);
      alert("Erreur lors de l'ajout de la facture");
    }
  };

  const handleEditFacture = async () => {
    if (!selectedFacture) return;

    try {
      const factureRef = doc(db, "factures", selectedFacture.id);
      const factureData = {
        fournisseurId: formData.fournisseurId,
        restaurantId: restaurantId,
        numero: formData.numero,
        achatHTVA: parseNumber(formData.achatHTVA),
        tvaDeductible: parseNumber(formData.tvaDeductible),
        achatTTC: parseNumber(formData.achatTTC),
        montantPaye: parseNumber(formData.montantPaye),
        statut: formData.statut,
        resteAPayer: parseNumber(formData.resteAPayer),
        modeDePaiement: formData.modeDePaiement,
        paymentnum: formData.paymentnum || '',
        date: formData.date,
        retouralasource: parseNumber(formData.retouralasource)
      };

      await updateDoc(factureRef, factureData);
      setOpenEditDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error editing facture:", error);
      alert("Erreur lors de la modification de la facture");
    }
  };

  const handleDeleteFacture = async () => {
    if (!selectedFacture) return;

    try {
      const factureRef = doc(db, "factures", selectedFacture.id);
      await deleteDoc(factureRef);
      setOpenDeleteDialog(false);
      setSelectedFacture(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting facture:", error);
      alert("Erreur lors de la suppression de la facture");
    }
  };

  const resetForm = () => {
    setFormData({
      numero: '',
      achatHTVA: '',
      tvaDeductible: '',
      achatTTC: '',
      montantPaye: '',
      statut: 'Non Payée',
      resteAPayer: '',
      modeDePaiement: '',
      paymentnum: '',
      date: new Date().toISOString().split('T')[0],
      retouralasource: '',
      fournisseurId: ''
    });
    setSelectedFacture(null);
    setSelectedFournisseur(null);
  };
const openEdit = (facture) => {
  setSelectedFacture(facture);
  setFormData({
    numero: facture.numero,
    achatHTVA: parseFloat(facture.achatHTVA).toFixed(3),  // Format with 3 decimals
    tvaDeductible: parseFloat(facture.tvaDeductible || 0).toFixed(3),  // Format with 3 decimals
    achatTTC: parseFloat(facture.achatTTC).toFixed(3),
    montantPaye: parseFloat(facture.montantPaye).toFixed(3),
    statut: facture.statut,
    resteAPayer: parseFloat(facture.resteAPayer).toFixed(3),
    modeDePaiement: facture.modeDePaiement,
    paymentnum: facture.paymentnum || '',
    date: facture.date instanceof Date ? facture.date.toISOString().split('T')[0] : facture.date,
    retouralasource: parseFloat(facture.retouralasource || 0).toFixed(3),
    fournisseurId: facture.fournisseurId
  });
  setOpenEditDialog(true);
};

  const openDelete = (facture) => {
    setSelectedFacture(facture);
    setOpenDeleteDialog(true);
  };

  const openDetails = (facture) => {
    console.log("Opening details for:", facture);
    setSelectedFacture(facture);
    setOpenDetailsDialog(true);
  };

  const handleMonthChange = (offset) => {
    const newDate = new Date(currentYear, currentMonth + offset);
    setCurrentMonth(newDate.getMonth());
    setCurrentYear(newDate.getFullYear());
  };

  // Calculate totals
  const totals = filteredFactures.reduce((acc, facture) => ({
    htva: acc.htva + (parseFloat(facture.achatHTVA) || 0),
    ttc: acc.ttc + (parseFloat(facture.achatTTC) || 0),
    paye: acc.paye + (parseFloat(facture.montantPaye) || 0),
    retenu: acc.retenu + (parseFloat(facture.retouralasource) || 0),
    reste: acc.reste + (parseFloat(facture.resteAPayer) || 0)
  }), { htva: 0, ttc: 0, paye: 0, retenu: 0, reste: 0 });

  const getFournisseurName = (fournisseurId) => {
    const fournisseur = fournisseurs.find(f => f.id === fournisseurId);
    return fournisseur?.name || 'Inconnu';
  };

  return (
    <Box sx={{ p: 1.5 }}>
      {/* Header with Add Button and Export - Compact */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2,
        flexWrap: 'wrap',
        gap: 1.5
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: alpha('#6366f1', 0.1) }}>
            <Receipt sx={{ fontSize: 18, color: '#6366f1' }} />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} fontSize="1rem">
              Factures Fournisseurs
            </Typography>
            <Typography variant="caption" color="textSecondary" fontSize="0.7rem">
              Gérez toutes vos factures fournisseurs
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="contained"
            size="small"
            startIcon={<Add sx={{ fontSize: 16 }} />}
            onClick={() => {
              resetForm();
              setOpenAddDialog(true);
            }}
            sx={{ 
              borderRadius: 1.5,
              px: 1.5,
              py: 0.5,
              fontSize: '0.7rem',
              background: 'linear-gradient(45deg, #6366f1, #8b5cf6)',
            }}
          >
            Nouvelle Facture
          </Button>
          
          <Button 
            variant="outlined"
            size="small"
            startIcon={<FileDownload sx={{ fontSize: 14 }} />}
            onClick={exportToExcel}
            sx={{ 
              borderRadius: 1.5,
              px: 1.5,
              py: 0.5,
              fontSize: '0.7rem',
              borderColor: '#6366f1',
              color: '#6366f1'
            }}
          >
            Exporter
          </Button>
        </Box>
      </Box>

      {/* Filters Card - Compact */}
      <Paper sx={{ 
        p: 1.5, 
        mb: 2, 
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider'
      }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '0.8rem' }}>Fournisseur</InputLabel>
              <Select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                label="Fournisseur"
                sx={{ borderRadius: 1.5, fontSize: '0.8rem' }}
                size="small"
              >
                <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Tous fournisseurs</MenuItem>
                {fournisseurs.map(fournisseur => (
                  <MenuItem key={fournisseur.id} value={fournisseur.name} sx={{ fontSize: '0.8rem' }}>
                    {fournisseur.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '0.8rem' }}>Statut</InputLabel>
              <Select
                value={searchStatue}
                onChange={(e) => setSearchStatue(e.target.value)}
                label="Statut"
                sx={{ borderRadius: 1.5, fontSize: '0.8rem' }}
                size="small"
              >
                <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Tous statuts</MenuItem>
                <MenuItem value="Payée" sx={{ fontSize: '0.8rem' }}>Payée</MenuItem>
                <MenuItem value="Non Payée" sx={{ fontSize: '0.8rem' }}>Non Payée</MenuItem>
                <MenuItem value="En retard" sx={{ fontSize: '0.8rem' }}>En retard</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'flex-end' }}>
              <IconButton 
                onClick={() => handleMonthChange(-1)}
                size="small"
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 0.5 }}
              >
                <ChevronLeft sx={{ fontSize: 18 }} />
              </IconButton>
              
              <Paper variant="outlined" sx={{ borderRadius: 1.5, px: 1.5, py: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarToday sx={{ fontSize: 14, color: '#6366f1' }} />
                  <Typography variant="caption" fontWeight={600} fontSize="0.75rem">
                    {new Date(currentYear, currentMonth).toLocaleString('fr-FR', { month: 'long', year: 'numeric' })}
                  </Typography>
                </Box>
              </Paper>

              <IconButton 
                onClick={() => handleMonthChange(1)}
                size="small"
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 0.5 }}
              >
                <ChevronRight sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Totals Row - Compact */}
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={2.4}>
          <Paper sx={{ p: 1, borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="textSecondary" fontSize="0.65rem">Total HTVA</Typography>
            <Typography variant="subtitle2" fontWeight={700} color="#6366f1" fontSize="0.85rem">
              {totals.htva.toFixed(3)} TND
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Paper sx={{ p: 1, borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="textSecondary" fontSize="0.65rem">Total TTC</Typography>
            <Typography variant="subtitle2" fontWeight={700} color="#6366f1" fontSize="0.85rem">
              {totals.ttc.toFixed(3)} TND
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Paper sx={{ p: 1, borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="textSecondary" fontSize="0.65rem">Total Payé</Typography>
            <Typography variant="subtitle2" fontWeight={700} color="#10b981" fontSize="0.85rem">
              {totals.paye.toFixed(3)} TND
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Paper sx={{ p: 1, borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="textSecondary" fontSize="0.65rem">Retenu source</Typography>
            <Typography variant="subtitle2" fontWeight={700} color="#f59e0b" fontSize="0.85rem">
              {totals.retenu.toFixed(3)} TND
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Paper sx={{ p: 1, borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="textSecondary" fontSize="0.65rem">Reste à Payer</Typography>
            <Typography variant="subtitle2" fontWeight={700} color="#ef4444" fontSize="0.85rem">
              {totals.reste.toFixed(3)} TND
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Table - Compact */}
      {loading ? (
        <LinearProgress sx={{ borderRadius: 1.5 }} />
      ) : (
        <>
          <TableContainer component={Paper} sx={{ 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'auto'
          }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#F9FAFB', fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Date</TableCell>
                  <TableCell sx={{ bgcolor: '#F9FAFB', fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Numéro</TableCell>
                  <TableCell sx={{ bgcolor: '#F9FAFB', fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Fournisseur</TableCell>
                  <TableCell sx={{ bgcolor: '#F9FAFB', fontWeight: 600, fontSize: '0.75rem', py: 1 }} align="right">HTVA</TableCell>
                  <TableCell sx={{ bgcolor: '#F9FAFB', fontWeight: 600, fontSize: '0.75rem', py: 1 }} align="right">TTC</TableCell>
                  <TableCell sx={{ bgcolor: '#F9FAFB', fontWeight: 600, fontSize: '0.75rem', py: 1 }} align="right">Payé</TableCell>
                  <TableCell sx={{ bgcolor: '#F9FAFB', fontWeight: 600, fontSize: '0.75rem', py: 1 }} align="right">Retenu</TableCell>
                  <TableCell sx={{ bgcolor: '#F9FAFB', fontWeight: 600, fontSize: '0.75rem', py: 1 }} align="right">Reste</TableCell>
                  <TableCell sx={{ bgcolor: '#F9FAFB', fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Statut</TableCell>
                  <TableCell sx={{ bgcolor: '#F9FAFB', fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Paiement</TableCell>
                  <TableCell sx={{ bgcolor: '#F9FAFB', fontWeight: 600, fontSize: '0.75rem', py: 1 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedFactures.map((facture) => (
                  <TableRow key={facture.id} hover sx={{ '& td': { py: 0.75, fontSize: '0.75rem' } }}>
                    <TableCell sx={{ fontSize: '0.7rem' }}>
                      {facture.date instanceof Date ? facture.date.toLocaleDateString('fr-FR') : new Date(facture.date).toLocaleDateString('fr-FR')}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} fontSize="0.7rem">
                        {facture.numero}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getFournisseurName(facture.fournisseurId)}
                        size="small"
                        sx={{ bgcolor: alpha('#6366f1', 0.1), color: '#6366f1', fontSize: '0.65rem', height: 22 }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.7rem' }}>{parseFloat(facture.achatHTVA).toFixed(3)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>{parseFloat(facture.achatTTC).toFixed(3)}</TableCell>
                    <TableCell align="right" sx={{ color: '#10b981', fontSize: '0.7rem' }}>{parseFloat(facture.montantPaye).toFixed(3)}</TableCell>
                    <TableCell align="right" sx={{ color: '#f59e0b', fontSize: '0.7rem' }}>{parseFloat(facture.retouralasource || 0).toFixed(3)}</TableCell>
                    <TableCell align="right" sx={{ color: parseFloat(facture.resteAPayer) > 0 ? '#ef4444' : '#10b981', fontSize: '0.7rem' }}>
                      {parseFloat(facture.resteAPayer).toFixed(3)}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={facture.statut} />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={facture.modeDePaiement || 'Non spécifié'}
                        size="small"
                        sx={{ 
                          fontSize: '0.6rem',
                          height: 22,
                          bgcolor: alpha('#6366f1', 0.1),
                          color: '#6366f1'
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Voir détails">
                          <IconButton size="small" sx={{ p: 0.5 }} onClick={() => openDetails(facture)}>
                            <Visibility sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Modifier">
                          <IconButton size="small" sx={{ p: 0.5 }} onClick={() => openEdit(facture)}>
                            <Edit sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton size="small" sx={{ p: 0.5 }} onClick={() => openDelete(facture)}>
                            <Delete sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {filteredFactures.length === 0 && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Receipt sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
                <Typography variant="body2" color="textSecondary">
                  Aucune facture trouvée
                </Typography>
              </Box>
            )}
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredFactures.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Lignes"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
            sx={{ '.MuiTablePagination-selectLabel': { fontSize: '0.7rem' }, '.MuiTablePagination-displayedRows': { fontSize: '0.7rem' } }}
          />
        </>
      )}

      {/* Add Facture Dialog */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: 'white',
          py: 1.5,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 28, height: 28 }}>
              <Add sx={{ fontSize: 18 }} />
            </Avatar>
            <Typography variant="subtitle1" fontWeight={700}>Nouvelle Facture</Typography>
          </Box>
          <IconButton onClick={() => setOpenAddDialog(false)} sx={{ color: 'white', p: 0.5 }}>
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 2 }}>
          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Fournisseur *</InputLabel>
                <Select
                  value={formData.fournisseurId}
                  onChange={(e) => setFormData({ ...formData, fournisseurId: e.target.value })}
                  label="Fournisseur *"
                  size="small"
                >
                  {fournisseurs.map(f => (
                    <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Numéro de Facture *"
                value={formData.numero}
                onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                sx={{ '& .MuiInputLabel-root': { fontSize: '0.8rem' }, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputLabel-root': { fontSize: '0.8rem' }, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}><Chip label="Montants" size="small" sx={{ fontSize: '0.7rem' }} /></Divider>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                type="text"
                label="HTVA *"
                value={formData.achatHTVA}
                onChange={(e) => setFormData({ ...formData, achatHTVA: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: '0.7rem' } }}>TND</InputAdornment> }}
                helperText="Utilisez . ou ,"
                sx={{ '& .MuiInputLabel-root': { fontSize: '0.8rem' }, '& .MuiInputBase-input': { fontSize: '0.8rem' }, '& .MuiFormHelperText-root': { fontSize: '0.65rem' } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                type="text"
                label="TVA"
                value={formData.tvaDeductible}
                onChange={(e) => setFormData({ ...formData, tvaDeductible: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: '0.7rem' } }}>TND</InputAdornment> }}
                helperText="Utilisez . ou ,"
                sx={{ '& .MuiInputLabel-root': { fontSize: '0.8rem' }, '& .MuiInputBase-input': { fontSize: '0.8rem' }, '& .MuiFormHelperText-root': { fontSize: '0.65rem' } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="TTC"
                value={formData.achatTTC}
                InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: '0.7rem' } }}>TND</InputAdornment> }}
                sx={{ '& .MuiInputLabel-root': { fontSize: '0.8rem' }, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                type="text"
                label="Montant Payé"
                value={formData.montantPaye}
                onChange={(e) => setFormData({ ...formData, montantPaye: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: '0.7rem' } }}>TND</InputAdornment> }}
                helperText="Utilisez . ou ,"
                sx={{ '& .MuiInputLabel-root': { fontSize: '0.8rem' }, '& .MuiInputBase-input': { fontSize: '0.8rem' }, '& .MuiFormHelperText-root': { fontSize: '0.65rem' } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                type="text"
                label="Retenu à la source"
                value={formData.retouralasource}
                onChange={(e) => setFormData({ ...formData, retouralasource: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: '0.7rem' } }}>TND</InputAdornment> }}
                helperText="Utilisez . ou ,"
                sx={{ '& .MuiInputLabel-root': { fontSize: '0.8rem' }, '& .MuiInputBase-input': { fontSize: '0.8rem' }, '& .MuiFormHelperText-root': { fontSize: '0.65rem' } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Reste à Payer"
                value={formData.resteAPayer}
                InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: '0.7rem' } }}>TND</InputAdornment> }}
                sx={{ '& .MuiInputLabel-root': { fontSize: '0.8rem' }, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}><Chip label="Paiement" size="small" sx={{ fontSize: '0.7rem' }} /></Divider>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Mode de Paiement</InputLabel>
                <Select
                  value={formData.modeDePaiement}
                  onChange={(e) => setFormData({ ...formData, modeDePaiement: e.target.value })}
                  label="Mode de Paiement"
                >
                  <MenuItem value="Chèque">Chèque</MenuItem>
                  <MenuItem value="Espèce">Espèce</MenuItem>
                  <MenuItem value="Virement Bancaire">Virement Bancaire</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {(formData.modeDePaiement === 'Chèque' || formData.modeDePaiement === 'Virement Bancaire') && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Numéro de Paiement"
                  value={formData.paymentnum}
                  onChange={(e) => setFormData({ ...formData, paymentnum: e.target.value })}
                  sx={{ '& .MuiInputLabel-root': { fontSize: '0.8rem' }, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setOpenAddDialog(false)} variant="outlined" size="small" startIcon={<Cancel sx={{ fontSize: 16 }} />}>
            Annuler
          </Button>
          <Button onClick={handleAddFacture} variant="contained" size="small" startIcon={<Save sx={{ fontSize: 16 }} />} sx={{ bgcolor: '#6366f1' }}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Facture Dialog - FIXED TVA FIELD */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: 'white',
          py: 1.5,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 28, height: 28 }}>
              <Edit sx={{ fontSize: 18 }} />
            </Avatar>
            <Typography variant="subtitle1" fontWeight={700}>Modifier Facture</Typography>
          </Box>
          <IconButton onClick={() => setOpenEditDialog(false)} sx={{ color: 'white', p: 0.5 }}>
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 2 }}>
          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Fournisseur *</InputLabel>
                <Select
                  value={formData.fournisseurId}
                  onChange={(e) => setFormData({ ...formData, fournisseurId: e.target.value })}
                  label="Fournisseur *"
                  size="small"
                >
                  {fournisseurs.map(f => (
                    <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Numéro de Facture *"
                value={formData.numero}
                onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                sx={{ '& .MuiInputLabel-root': { fontSize: '0.8rem' }, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputLabel-root': { fontSize: '0.8rem' }, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}><Chip label="Montants" size="small" sx={{ fontSize: '0.7rem' }} /></Divider>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                type="text"
                label="HTVA *"
                value={formData.achatHTVA}
                onChange={(e) => setFormData({ ...formData, achatHTVA: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: '0.7rem' } }}>TND</InputAdornment> }}
                helperText="Utilisez . ou ,"
                sx={{ '& .MuiInputLabel-root': { fontSize: '0.8rem' }, '& .MuiInputBase-input': { fontSize: '0.8rem' }, '& .MuiFormHelperText-root': { fontSize: '0.65rem' } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                type="text"
                label="TVA"
                value={formData.tvaDeductible} 
                onChange={(e) => setFormData({ ...formData, tvaDeductible: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: '0.7rem' } }}>TND</InputAdornment> }}
                helperText="Utilisez . ou ,"
                sx={{ '& .MuiInputLabel-root': { fontSize: '0.8rem' }, '& .MuiInputBase-input': { fontSize: '0.8rem' }, '& .MuiFormHelperText-root': { fontSize: '0.65rem' } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="TTC"
                value={formData.achatTTC}
                InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: '0.7rem' } }}>TND</InputAdornment> }}
                sx={{ '& .MuiInputLabel-root': { fontSize: '0.8rem' }, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                type="text"
                label="Montant Payé"
                value={formData.montantPaye}
                onChange={(e) => setFormData({ ...formData, montantPaye: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: '0.7rem' } }}>TND</InputAdornment> }}
                helperText="Utilisez . ou ,"
                sx={{ '& .MuiInputLabel-root': { fontSize: '0.8rem' }, '& .MuiInputBase-input': { fontSize: '0.8rem' }, '& .MuiFormHelperText-root': { fontSize: '0.65rem' } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                type="text"
                label="Retenu à la source"
                value={formData.retouralasource}
                onChange={(e) => setFormData({ ...formData, retouralasource: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: '0.7rem' } }}>TND</InputAdornment> }}
                helperText="Utilisez . ou ,"
                sx={{ '& .MuiInputLabel-root': { fontSize: '0.8rem' }, '& .MuiInputBase-input': { fontSize: '0.8rem' }, '& .MuiFormHelperText-root': { fontSize: '0.65rem' } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Reste à Payer"
                value={formData.resteAPayer}
                InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: '0.7rem' } }}>TND</InputAdornment> }}
                sx={{ '& .MuiInputLabel-root': { fontSize: '0.8rem' }, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}><Chip label="Paiement" size="small" sx={{ fontSize: '0.7rem' }} /></Divider>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Mode de Paiement</InputLabel>
                <Select
                  value={formData.modeDePaiement}
                  onChange={(e) => setFormData({ ...formData, modeDePaiement: e.target.value })}
                  label="Mode de Paiement"
                >
                  <MenuItem value="Chèque">Chèque</MenuItem>
                  <MenuItem value="Espèce">Espèce</MenuItem>
                  <MenuItem value="Virement Bancaire">Virement Bancaire</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {(formData.modeDePaiement === 'Chèque' || formData.modeDePaiement === 'Virement Bancaire') && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Numéro de Paiement"
                  value={formData.paymentnum}
                  onChange={(e) => setFormData({ ...formData, paymentnum: e.target.value })}
                  sx={{ '& .MuiInputLabel-root': { fontSize: '0.8rem' }, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setOpenEditDialog(false)} variant="outlined" size="small" startIcon={<Cancel sx={{ fontSize: 16 }} />}>
            Annuler
          </Button>
          <Button onClick={handleEditFacture} variant="contained" size="small" startIcon={<Save sx={{ fontSize: 16 }} />} sx={{ bgcolor: '#6366f1' }}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog - Compact */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle sx={{ textAlign: 'center', pt: 2, pb: 1 }}>
          <Avatar sx={{ margin: '0 auto', bgcolor: '#ef4444', width: 48, height: 48 }}>
            <DeleteForever sx={{ fontSize: 24 }} />
          </Avatar>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1 }}>
            Confirmer la suppression
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography align="center" fontSize="0.85rem">
            Supprimer la facture <strong>{selectedFacture?.numero}</strong> ?
            <br />
            <Typography variant="caption" color="error" fontSize="0.7rem">
              Action irréversible
            </Typography>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button onClick={() => setOpenDeleteDialog(false)} variant="outlined" size="small">
            Annuler
          </Button>
          <Button onClick={handleDeleteFacture} variant="contained" color="error" size="small">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog 
        open={openDetailsDialog} 
        onClose={() => setOpenDetailsDialog(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: 'white',
          py: 1.5
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1" fontWeight={700} fontSize="0.9rem">
              Détails Facture
            </Typography>
            <IconButton onClick={() => setOpenDetailsDialog(false)} sx={{ color: 'white', p: 0.5 }}>
              <Close sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {selectedFacture ? (
            <Stack spacing={1.5}>
              <Paper sx={{ p: 1.5, bgcolor: '#F9FAFB', borderRadius: 1.5 }}>
                <Typography variant="caption" color="#6366f1" fontWeight={600} fontSize="0.7rem">Informations Générales</Typography>
                <Divider sx={{ my: 0.5 }} />
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" fontSize="0.65rem">Numéro Facture</Typography>
                    <Typography variant="body2" fontWeight={600} fontSize="0.8rem">{selectedFacture.numero}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" fontSize="0.65rem">Date</Typography>
                    <Typography variant="body2" fontSize="0.8rem">
                      {selectedFacture.date instanceof Date 
                        ? selectedFacture.date.toLocaleDateString('fr-FR') 
                        : new Date(selectedFacture.date).toLocaleDateString('fr-FR')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary" fontSize="0.65rem">Fournisseur</Typography>
                    <Typography variant="body2" fontWeight={600} fontSize="0.8rem">
                      {getFournisseurName(selectedFacture.fournisseurId)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Paper sx={{ p: 1.5, bgcolor: '#F9FAFB', borderRadius: 1.5 }}>
                <Typography variant="caption" color="#6366f1" fontWeight={600} fontSize="0.7rem">Montants</Typography>
                <Divider sx={{ my: 0.5 }} />
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" fontSize="0.65rem">HTVA</Typography>
                    <Typography variant="body2" fontSize="0.8rem">{parseFloat(selectedFacture.achatHTVA).toFixed(3)} TND</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" fontSize="0.65rem">TVA</Typography>
                    <Typography variant="body2" fontSize="0.8rem">{parseFloat(selectedFacture.tvaDeductible || 0).toFixed(3)} TND</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" fontSize="0.65rem">TTC</Typography>
                    <Typography variant="body2" fontWeight={600} fontSize="0.8rem">{parseFloat(selectedFacture.achatTTC).toFixed(3)} TND</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" fontSize="0.65rem">Montant Payé</Typography>
                    <Typography variant="body2" sx={{ color: '#10b981' }} fontSize="0.8rem">{parseFloat(selectedFacture.montantPaye).toFixed(3)} TND</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" fontSize="0.65rem">Retenu à la source</Typography>
                    <Typography variant="body2" sx={{ color: '#f59e0b' }} fontSize="0.8rem">{parseFloat(selectedFacture.retouralasource || 0).toFixed(3)} TND</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" fontSize="0.65rem">Reste à Payer</Typography>
                    <Typography variant="body2" sx={{ color: parseFloat(selectedFacture.resteAPayer) > 0 ? '#ef4444' : '#10b981' }} fontSize="0.8rem">
                      {parseFloat(selectedFacture.resteAPayer).toFixed(3)} TND
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Paper sx={{ p: 1.5, bgcolor: '#F9FAFB', borderRadius: 1.5 }}>
                <Typography variant="caption" color="#6366f1" fontWeight={600} fontSize="0.7rem">Paiement</Typography>
                <Divider sx={{ my: 0.5 }} />
                <Stack spacing={0.5}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="textSecondary" fontSize="0.65rem">Statut</Typography>
                    <StatusChip status={selectedFacture.statut} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="textSecondary" fontSize="0.65rem">Mode de Paiement</Typography>
                    <Typography variant="body2" fontSize="0.75rem">{selectedFacture.modeDePaiement || 'Non spécifié'}</Typography>
                  </Box>
                  {selectedFacture.paymentnum && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="textSecondary" fontSize="0.65rem">Numéro de Paiement</Typography>
                      <Typography variant="body2" fontSize="0.75rem">{selectedFacture.paymentnum}</Typography>
                    </Box>
                  )}
                </Stack>
              </Paper>
            </Stack>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="textSecondary">
                Aucune information disponible
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setOpenDetailsDialog(false)} 
            variant="contained" 
            fullWidth 
            size="small" 
            sx={{ bgcolor: '#6366f1' }}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExcelTableFournisseur;